import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Check, Loader2, CreditCard, Sparkles, Euro, ExternalLink, Landmark } from 'lucide-react';

const SubscriptionTab = () => {
  const { API, getAuthHeaders } = useAuth();
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(14);
  const [sub, setSub] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [busy, setBusy] = useState(null);
  const [deposit, setDeposit] = useState(null);
  const [connect, setConnect] = useState(null);
  const [savingDeposit, setSavingDeposit] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, s, d, c] = await Promise.all([
        axios.get(`${API}/billing/plans`),
        axios.get(`${API}/billing/subscription`, getAuthHeaders()),
        axios.get(`${API}/billing/deposit-settings`, getAuthHeaders()),
        axios.get(`${API}/billing/connect`, getAuthHeaders()),
      ]);
      setPlans(p.data.plans);
      setTrialDays(p.data.trial_days);
      setSub(s.data);
      setDeposit(d.data);
      setConnect(c.data);
    } catch {
      toast.error('Caricamento del listino non riuscito');
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const subscribe = async (lookupKey) => {
    setBusy(lookupKey);
    try {
      const res = await axios.post(`${API}/billing/checkout`,
        { lookup_key: lookupKey, origin_url: window.location.origin }, getAuthHeaders());
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Pagamento non avviabile');
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy('portal');
    try {
      const res = await axios.post(`${API}/billing/portal`,
        { origin_url: window.location.origin }, getAuthHeaders());
      window.location.href = res.data.portal_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gestione non disponibile');
      setBusy(null);
    }
  };

  const startConnect = async () => {
    setBusy('connect');
    try {
      const res = await axios.post(`${API}/billing/connect/onboarding`,
        { origin_url: window.location.origin }, getAuthHeaders());
      window.location.href = res.data.onboarding_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Attivazione non disponibile', { duration: 10000 });
      setBusy(null);
    }
  };

  const saveDeposit = async () => {
    setSavingDeposit(true);
    try {
      await axios.put(`${API}/billing/deposit-settings`, deposit, getAuthHeaders());
      toast.success('Acconto aggiornato');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Salvataggio non riuscito');
    } finally {
      setSavingDeposit(false);
    }
  };

  const active = sub?.state === 'active';

  return (
    <div className="space-y-6 max-w-5xl" data-testid="billing-page">
        <p className="text-sm text-muted-foreground">Il tuo piano e i pagamenti che incassi dai clienti</p>

        {sub && (
          <Card data-testid="subscription-state">
            <CardContent className="pt-6 flex flex-wrap items-center gap-4 justify-between">
              <div>
                {active ? (
                  <>
                    <p className="font-semibold flex items-center gap-2">
                      Piano {sub.plan?.charAt(0).toUpperCase() + sub.plan?.slice(1)}
                      <Badge variant="secondary">{sub.interval === 'year' ? 'annuale' : 'mensile'}</Badge>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {sub.cancel_at_period_end ? 'Si chiude il ' : 'Si rinnova il '}
                      {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString('it-IT') : '—'}
                    </p>
                  </>
                ) : sub.state === 'trial' ? (
                  <>
                    <p className="font-semibold">Prova gratuita in corso</p>
                    <p className="text-sm text-muted-foreground" data-testid="trial-days-left">
                      Ti restano {sub.days_left} giorni, poi scegli un piano quando vuoi.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold">Prova terminata</p>
                    <p className="text-sm text-muted-foreground">
                      Puoi continuare a lavorare: attiva un piano quando ti torna comodo.
                    </p>
                  </>
                )}
              </div>
              {active && (
                <Button variant="outline" onClick={openPortal} disabled={busy === 'portal'}
                  className="min-h-[44px]" data-testid="manage-subscription-btn">
                  {busy === 'portal' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                  Gestisci, fatture e disdetta
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex items-center gap-3">
          <button onClick={() => setYearly(false)} data-testid="billing-monthly-btn"
            className={`px-4 min-h-[44px] rounded-full text-sm transition-colors ${!yearly ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
            Mensile
          </button>
          <button onClick={() => setYearly(true)} data-testid="billing-yearly-btn"
            className={`px-4 min-h-[44px] rounded-full text-sm transition-colors ${yearly ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
            Annuale
            {plans[0]?.months_free > 0 && (
              <span className="ml-2 text-xs opacity-80">{plans[0].months_free} mesi in regalo</span>
            )}
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-3" data-testid="plans-grid">
          {plans.map(plan => {
            const key = yearly ? plan.yearly : plan.monthly;
            const price = yearly ? plan.yearly_per_month : plan.monthly_price;
            const isCurrent = active && sub.plan === plan.id && sub.interval === (yearly ? 'year' : 'month');
            return (
              <Card key={plan.id} className={plan.popular ? 'border-primary shadow-md relative' : 'relative'}
                data-testid={`plan-${plan.id}`}>
                {plan.popular && (
                  <Badge className="absolute -top-2 left-4 gap-1"><Sparkles className="w-3 h-3" /> Il più scelto</Badge>
                )}
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                  <p className="text-xs text-muted-foreground">{plan.tagline}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <span className="text-3xl font-bold">€{price.toFixed(0)}</span>
                    <span className="text-sm text-muted-foreground">/mese</span>
                    {yearly && (
                      <p className="text-xs text-muted-foreground mt-1">
                        €{plan.yearly_price.toFixed(0)} una volta l'anno
                      </p>
                    )}
                  </div>
                  <ul className="space-y-1.5 text-sm">
                    {plan.features.map(f => (
                      <li key={f} className="flex gap-2">
                        <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full min-h-[44px]" variant={plan.popular ? 'default' : 'outline'}
                    disabled={!!busy || isCurrent} onClick={() => subscribe(key)}
                    data-testid={`subscribe-${plan.id}`}>
                    {busy === key ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    {isCurrent ? 'Piano attuale' : active ? 'Passa a questo piano' : `Prova ${trialDays} giorni gratis`}
                  </Button>
                  {!active && (
                    <p className="text-[11px] text-center text-muted-foreground">
                      Nessun addebito per {trialDays} giorni, disdici quando vuoi
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {connect && (
          <Card data-testid="connect-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Landmark className="w-4 h-4 text-primary" /> Dove arrivano gli incassi dei clienti
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {connect.available === false ? (
                <div className="space-y-3" data-testid="connect-blocker">
                  <p className="text-sm text-muted-foreground">{connect.blocker}</p>
                </div>
              ) : connect.charges_enabled ? (
                <>
                  <p className="text-sm flex items-center gap-2" data-testid="connect-ready">
                    <Check className="w-4 h-4 text-primary" />
                    Gift card e acconti arrivano sul tuo conto, con i tuoi versamenti Stripe.
                  </p>
                  {!connect.payouts_enabled && (
                    <p className="text-xs text-amber-600">
                      I versamenti sul conto corrente sono ancora in verifica: gli incassi restano al sicuro su Stripe.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    {connect.connected
                      ? 'Attivazione da completare: finché non è pronta, gift card e acconti restano in sospeso sul conto della piattaforma.'
                      : 'Collega il tuo conto per incassare gift card e acconti direttamente, senza passaggi intermedi.'}
                  </p>
                  {connect.requirements?.length > 0 && (
                    <p className="text-xs text-muted-foreground" data-testid="connect-requirements">
                      Stripe sta ancora aspettando {connect.requirements.length} informazion{connect.requirements.length === 1 ? 'e' : 'i'}.
                    </p>
                  )}
                  <Button onClick={startConnect} disabled={busy === 'connect'} className="min-h-[44px]"
                    data-testid="connect-onboarding-btn">
                    {busy === 'connect' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Landmark className="w-4 h-4 mr-2" />}
                    {connect.connected ? 'Completa l\'attivazione' : 'Collega il mio conto'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {deposit && (
          <Card data-testid="deposit-settings">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Euro className="w-4 h-4 text-primary" /> Acconto sulle prenotazioni online
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium">Chiedi un acconto per prenotare</p>
                  <p className="text-xs text-muted-foreground">
                    Chi prenota dal sito paga subito una parte: meno buchi in agenda.
                  </p>
                </div>
                <Switch checked={deposit.enabled} data-testid="deposit-enabled-switch"
                  onCheckedChange={v => setDeposit({ ...deposit, enabled: v })} />
              </div>
              {deposit.enabled && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Come lo calcolo</Label>
                    <Select value={deposit.type} onValueChange={v => setDeposit({ ...deposit, type: v })}>
                      <SelectTrigger data-testid="deposit-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percent">In percentuale sul servizio</SelectItem>
                        <SelectItem value="fixed">Cifra fissa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{deposit.type === 'percent' ? 'Percentuale' : 'Euro'}</Label>
                    <Input type="number" min="1" value={deposit.value} data-testid="deposit-value-input"
                      onChange={e => setDeposit({ ...deposit, value: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Disdetta gratuita fino a</Label>
                    <Select value={String(deposit.free_cancel_hours)}
                      onValueChange={v => setDeposit({ ...deposit, free_cancel_hours: parseInt(v, 10) })}>
                      <SelectTrigger data-testid="deposit-hours-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Mai: acconto sempre trattenuto</SelectItem>
                        <SelectItem value="12">12 ore prima</SelectItem>
                        <SelectItem value="24">24 ore prima</SelectItem>
                        <SelectItem value="48">2 giorni prima</SelectItem>
                        <SelectItem value="72">3 giorni prima</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground sm:col-span-2" data-testid="deposit-policy-preview">
                    Il cliente legge: «{deposit.free_cancel_hours > 0
                      ? `Disdici gratis fino a ${deposit.free_cancel_hours >= 48 ? `${deposit.free_cancel_hours / 24} giorni` : `${deposit.free_cancel_hours} ore`} prima: l'acconto ti viene restituito. Dopo, o se non ti presenti, l'acconto resta al salone.`
                      : "L'acconto non è rimborsabile in caso di disdetta o mancata presentazione."}»
                  </p>
                  <div className="flex items-center justify-between gap-4 sm:col-span-2 border-t pt-3">
                    <div>
                      <p className="text-sm font-medium">Avvisa il cliente quando trattieni l'acconto</p>
                      <p className="text-xs text-muted-foreground">
                        Gli arriva un messaggio gentile con il motivo e l'invito a riprenotare.
                      </p>
                    </div>
                    <Switch checked={deposit.notify_kept} data-testid="deposit-notify-switch"
                      onCheckedChange={v => setDeposit({ ...deposit, notify_kept: v })} />
                  </div>
                </div>
              )}
              <Button onClick={saveDeposit} disabled={savingDeposit} className="min-h-[44px]"
                data-testid="save-deposit-btn">
                {savingDeposit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salva
              </Button>
            </CardContent>
          </Card>
        )}
    </div>
  );
};

export default SubscriptionTab;
